import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchCourses,
  fetchCourseSettings
} from "../../../../redux/actions/contentCoursesActions";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import UneditableField from "../../../../components/NonEditableField/NonEditableField";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import Modal from "../../../../components/Modal/Modal";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles(theme => ({
  image: {
    border: `solid 1px ${theme.palette.secondary[300]}`,
    width: 225,
    height: 225,
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "unset"
    }
  }
}));

const courseSelector = makeGetEntities();

const CourseInfoModal = ({ open, onClose, courseId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { courseData, courseSettings, loading } = useSelector(
    ({ contentCoursesState }) => ({
      courseData: courseSelector(contentCoursesState, {
        type: "trainingObject",
        id: courseId
      }),
      courseSettings: courseSelector(contentCoursesState, {
        type: "courseSettings",
        id: courseId
      }),
      loading: contentCoursesState.loading
    })
  );

  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourseSettings(courseId));
      dispatch(fetchCourses(courseId));
    }
  }, [courseId]);

  return (
    <Modal
      maxWidth={"md"}
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {courseData ? courseData.name : ""}
        </Typography>
      }
      fullWidth
      open={open}
      onClose={onClose}
      showCloseIcon
      scrollable
      dialogContent={
        <Box
          display="flex"
          flexDirection="column"
          py={3}
          pl={{ xs: 0, md: 3 }}
          pr={1}
          minHeight={400}
        >
          {loading ? (
            <LoadingSpinner />
          ) : courseData && courseSettings ? (
            <Grid container spacing={4} wrap="wrap-reverse">
              {courseSettings.courseImage && (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{ mr: { xs: 0, sm: 3, md: 0 }, mt: { xs: 2, sm: 0 } }}
                >
                  <img
                    className={classes.image}
                    alt={courseSettings.courseTitle}
                    src={courseSettings.courseImage}
                  />
                </Grid>
              )}
              <Grid container spacing={4} item xs={12} sm={8}>
                <Grid item xs={12}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsCourseTitleField"
                    )}
                    fieldValue={courseSettings.courseTitle}
                  />
                </Grid>
                <Grid item xs={12} sx={{ mb: 1.5 }}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsDescriptionField"
                    )}
                    fieldValue={courseSettings.description}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsDurationHoursField"
                    )}
                    fieldValue={courseSettings.durationHours}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsDurationMinutesField"
                    )}
                    fieldValue={courseSettings.durationMinutes}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsIncludesCertificateField"
                    )}
                    fieldValue={
                      courseSettings.includeCertificate === "yes"
                        ? t("yes")
                        : t("no")
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UneditableField
                    align="flex-start"
                    fieldName={t(
                      "manageCourses.courseSettingsRecordsScoreField"
                    )}
                    fieldValue={
                      courseSettings.recordScore === "yes" ? t("yes") : t("no")
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          ) : null}
        </Box>
      }
    />
  );
};

CourseInfoModal.propTypes = {
  courseId: PropTypes.string
};

export default CourseInfoModal;
